/*
* SoundLevelDynamicChart.js (Don't remove this comment)
* This component is used to display a dynamic chart of sound levels.
* It uses the ECharts library (https://echarts.apache.org/examples/en/editor.html?c=dynamic-data2)
* to create a line chart that updates in real-time.
* The chart displays the sound level in decibels (dB) over time.
* The chart is responsive and will resize when the window is resized.
* The chart is destroyed when the component is unmounted to prevent memory leaks.
* The chart is initialized with a title and a line series.
* The chart hozizontally time axis and a vertical sound level axis.
* The hozizontally max time axis is 24 hours.
*/

import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Box } from '@mui/material';

// 修改為接收 soundData 作為 props
const SoundLevelDynamicChart = ({ 
  title = 'Sound Level Monitoring',
  soundData = [], // 格式: [[timestamp, level], ...]
  minValue = 0,
  maxValue = 120
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  // Initialize the chart
  useEffect(() => {
    // Create chart instance
    const chart = echarts.init(chartRef.current);
    chartInstance.current = chart;
    
    // Set initial options
    const option = {
      title: {
        text: title,
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          const time = new Date(params[0].value[0]);
          const formattedTime = time.toLocaleTimeString();
          const soundLevel = params[0].value[1].toFixed(2);
          return `${formattedTime}<br/>Sound Level: ${soundLevel} dB`;
        },
        axisPointer: {
          animation: false
        }
      },
      xAxis: {
        type: 'time',
        splitLine: {
          show: false
        },
        axisLabel: {
          formatter: function (value) {
            const date = new Date(value);
            return date.getHours() + ':' + date.getMinutes().toString().padStart(2, '0');
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Sound Level (dB)',
        min: minValue,
        max: maxValue,
        splitLine: {
          show: true
        }
      },
      series: [{
        name: 'Sound Level',
        type: 'line',
        showSymbol: false,
        // 24 小時資料點多（歷史每分鐘 + 即時每秒），降採樣只影響繪圖，不改資料
        sampling: 'lttb',
        data: soundData,
        lineStyle: {
          width: 2,
          color: '#5470C6'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: 'rgba(84, 112, 198, 0.5)'
            },
            {
              offset: 1,
              color: 'rgba(84, 112, 198, 0.1)'
            }
          ])
        },
        emphasis: {
          focus: 'series'
        }
      }],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      }
    };
    
    chart.setOption(option);
    
    // Handle window resize
    const handleResize = () => {
      chart.resize();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [title, minValue, maxValue]); // 依賴項不包含 soundData，因為它會在下面單獨處理
  
  // 單獨處理 soundData 更新，避免圖表完全重新渲染
  useEffect(() => {
    const chart = chartInstance.current;
    if (chart) {
      chart.setOption({
        series: [{
          data: soundData
        }]
      });
    }
  }, [soundData]);
  
  // 確保在組件首次渲染後重新調整圖表大小
  useEffect(() => {
    const chart = chartInstance.current;
    if (chart) {
      chart.resize();
    }
  }, []);
  
  return (
    <Box 
      ref={chartRef} 
      sx={{ 
        width: '100%', 
        height: 400,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1
      }} 
    />
  );
};

export default SoundLevelDynamicChart;